import { type FC, memo, type RefObject } from 'react';

import { GameCard } from 'features/games';

import { useOpenType } from 'core/providers/OpenTypeContext';
import { GenericErrorBoundary } from 'shared/components/ErrorBoundaries/GenericErrorBoundary';
import { GameCardFallback } from 'shared/components/ErrorBoundaries/GameCardErrorBoundary/GameCardFallback';
import { useViewMode } from 'store/app/appStore';
import { LeftArrow, RightArrow } from '../ScrollArrows';

import styles from './TopSectionGrid.module.css';
import type IGameSettings from 'types/models/IGameSettings';

interface TopSectionGridProps {
  games: IGameSettings[];
  sectionName: string;
  hasJackpotPanel: boolean;
  gridContainerRef: RefObject<HTMLDivElement>;
  scrollArrows: {
    isHovered: boolean;
    showLeftArrow: boolean;
    showRightArrow: boolean;
  };
  scrollLeft: () => void;
  scrollRight: () => void;
}

const TopSectionGrid: FC<TopSectionGridProps> = ({
  games,
  sectionName,
  hasJackpotPanel,
  gridContainerRef,
  scrollArrows,
  scrollLeft,
  scrollRight,
}) => {
  const { openType } = useOpenType();
  const viewMode = useViewMode();

  return (
    <>
      <div
        className={styles.gamesGrid}
        data-has-jackpot={hasJackpotPanel}
        data-view={viewMode}
        open-type={openType}
        ref={gridContainerRef}
        style={games.length === 0 ? { grid: 'none' } : {}}
      >
        {games.map((game, index) => (
          <GenericErrorBoundary
            key={`${game.gameKey}_${index}`}
            type="game"
            identifier={`GameCard-${game.gameKey}`}
            fallback={(error, reset) => <GameCardFallback gameName={game.name} error={error} onRetry={reset} />}
          >
            <GameCard data={game} sectionName={sectionName} displayIndex={index + 1} sectionGames={games} />
          </GenericErrorBoundary>
        ))}
      </div>
      {scrollArrows.isHovered && (
        <>
          {scrollArrows.showLeftArrow && <LeftArrow onClick={scrollLeft} />}
          {scrollArrows.showRightArrow && <RightArrow onClick={scrollRight} />}
        </>
      )}
    </>
  );
};

export default memo(TopSectionGrid);
